import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import classnames from "classnames";
import { Box } from "../../Box/index.js";
import { useDebounce } from "../../hooks/index.js";
import { Icon } from "../../Icon/index.js";
import { Label, Preloader } from "../common/index.js";
import { useAutocompleteReducer } from "../hooks/index.js";
import { TextFieldMasked } from "../TextFieldMasked/index.js";
import MultipleSuggestedInput_styles from "./MultipleSuggestedInput.styles.js";
import { ListBox } from "./ListBox.js";
import { Tag } from "./Tag.js";
import { OptionText } from "./OptionText.js";
function _define_property(obj, key, value) {
    if (key in obj) Object.defineProperty(obj, key, {
        value: value,
        enumerable: true,
        configurable: true,
        writable: true
    });
    else obj[key] = value;
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = null != arguments[i] ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if ("function" == typeof Object.getOwnPropertySymbols) ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
            return Object.getOwnPropertyDescriptor(source, sym).enumerable;
        }));
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function MultipleSuggestedInput_ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) symbols = symbols.filter(function(sym) {
            return Object.getOwnPropertyDescriptor(object, sym).enumerable;
        });
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = null != source ? source : {};
    if (Object.getOwnPropertyDescriptors) Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    else MultipleSuggestedInput_ownKeys(Object(source)).forEach(function(key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
    return target;
}
const DEBOUNCE_TIMEOUT = 200;
const MultipleSuggestedInput = (props)=>{
    var _useDebounce;
    const { id, name, label, labelIcon, value, onChange, onBlur, placeholder, disabled = false, required = false, clearable = false, loading = false, tooltip, error = false, helperText, renderOption, limit = 50, minInputValueLength = 1, minInputValueLengthForRequest = minInputValueLength, mask } = props;
    const { classes } = MultipleSuggestedInput_styles();
    const reactId = useId();
    const inputId = useMemo(()=>null != id ? id : reactId, [
        id,
        reactId
    ]);
    const { items, status, hasMore, requestItems, resetItems } = useAutocompleteReducer('load' === props.type ? props.loadItems : void 0);
    const [isLoading, setLoading] = useState(false);
    const [filter, setFilter] = useState({
        limit,
        offset: 0,
        query: ''
    });
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const isItemsLoading = isLoading || 'loading' === status;
    const debouncedInputValue = null != (_useDebounce = useDebounce(inputValue, DEBOUNCE_TIMEOUT)) ? _useDebounce : '';
    const handleChange = useCallback((event, nextValue)=>{
        if (nextValue && nextValue.length >= minInputValueLength && '' !== nextValue.trim() && !(null == value ? void 0 : value.includes(nextValue.trim()))) {
            onChange([
                ...value || [],
                nextValue.trim()
            ]);
            setInputValue('');
            setOpen(false);
            resetItems();
            setFilter(_object_spread_props(_object_spread({}, filter), {
                offset: 0,
                query: ''
            }));
        }
    }, [
        onChange,
        filter,
        resetItems,
        value,
        minInputValueLength
    ]);
    const handleInputChange = useCallback((event, nextValue, reason)=>{
        if ('reset' === reason) {
            setInputValue('');
            setOpen(false);
            return;
        }
        setInputValue(nextValue);
        setFilter(_object_spread_props(_object_spread({}, filter), {
            query: nextValue
        }));
        if (null == nextValue ? void 0 : nextValue.replace(/_+/g, '').trim()) {
            if (!open) setOpen(true);
        } else setOpen(false);
    }, [
        open,
        filter
    ]);
    const handleBlur = useCallback(()=>{
        null == onBlur || onBlur();
        if (open) setOpen(false);
    }, [
        onBlur,
        open
    ]);
    const handleReset = (e)=>{
        e.stopPropagation();
        e.preventDefault();
        setInputValue('');
        handleBlur();
    };
    const handleOpen = useCallback(()=>{
        if (null == inputValue ? void 0 : inputValue.replace(/_+/g, '').trim()) setOpen(true);
    }, [
        inputValue
    ]);
    const handleDeleteTag = (index)=>()=>{
            if (!value) return;
            onChange([
                ...value.slice(0, index),
                ...value.slice(index + 1)
            ]);
        };
    const requestNextItems = ()=>{
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.offset + filter.limit
        }));
        null == requestItems || requestItems(filter, 'get');
    };
    useEffect(()=>{
        if ('local' === props.type || !open || debouncedInputValue.length < minInputValueLengthForRequest) return;
        const abortRequest = null == requestItems ? void 0 : requestItems(_object_spread_props(_object_spread({}, filter), {
            offset: 0,
            query: debouncedInputValue
        }), 'search');
        setFilter(_object_spread_props(_object_spread({}, filter), {
            offset: filter.limit
        }));
        setLoading(false);
        return ()=>{
            null == abortRequest || abortRequest();
        };
    }, [
        open,
        debouncedInputValue
    ]);
    const filterOptions = (optionList, state)=>{
        const cleanInput = state.inputValue.replace(/_/g, '').trim();
        const filteredOptions = optionList.filter((option)=>option.toLowerCase().includes(cleanInput.toLowerCase()) && option.toLowerCase() !== cleanInput.toLowerCase());
        return inputValue ? [
            inputValue,
            ...filteredOptions
        ] : filteredOptions;
    };
    return /*#__PURE__*/ jsxs(FormControl, {
        className: classnames(classes.suggestedInput, 'aqa_suggested_input'),
        children: [
            label && /*#__PURE__*/ jsx(Label, {
                label: label,
                required: required,
                tooltip: tooltip,
                icon: labelIcon,
                inputId: inputId
            }),
            /*#__PURE__*/ jsx(Autocomplete, {
                value: "",
                classes: {
                    paper: classes.paper,
                    option: classes.option
                },
                options: 'load' === props.type ? items : props.externalItems,
                filterOptions: filterOptions,
                fullWidth: true,
                freeSolo: true,
                renderTags: ()=>null,
                disabled: disabled || loading,
                disableClearable: !clearable,
                clearIcon: /*#__PURE__*/ jsx(Icon, {
                    name: "dismiss_substract",
                    size: "24",
                    color: "stroke.secondary"
                }),
                inputValue: loading ? 'Загрузка...' : inputValue,
                onInputChange: mask ? void 0 : handleInputChange,
                onChange: handleChange,
                open: inputValue.length >= minInputValueLengthForRequest ? open : false,
                onOpen: handleOpen,
                onClose: handleBlur,
                renderOption: (renderOptionProps, option)=>/*#__PURE__*/ jsx("li", _object_spread_props(_object_spread({}, renderOptionProps), {
                        children: renderOption ? renderOption(option) : /*#__PURE__*/ jsx(OptionText, {
                            option: option,
                            inputValue: inputValue
                        })
                    })),
                slotProps: {
                    listbox: _object_spread({
                        component: ListBox
                    }, {
                        dataLength: items.length,
                        next: requestNextItems,
                        hasMore,
                        loading: isItemsLoading,
                        long: !!renderOption
                    })
                },
                renderInput: (params)=>mask ? /*#__PURE__*/ jsx(TextFieldMasked, _object_spread_props(_object_spread({}, params), {
                        id: inputId,
                        mask: mask,
                        name: name,
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label,
                        loading: loading,
                        classes: {
                            root: classes.inputRoot
                        },
                        onChange: handleInputChange
                    })) : /*#__PURE__*/ jsx(TextField, _object_spread(_object_spread_props(_object_spread({}, params), {
                        id: inputId,
                        value: inputValue,
                        name: name,
                        classes: {
                            root: classes.inputRoot
                        },
                        autoComplete: "off",
                        variant: "outlined",
                        fullWidth: true,
                        error: error,
                        helperText: helperText,
                        placeholder: placeholder || label
                    }), loading ? {
                        slotProps: {
                            input: {
                                endAdornment: /*#__PURE__*/ jsx(Preloader, {})
                            }
                        }
                    } : {
                        InputProps: _object_spread_props(_object_spread({}, params.InputProps), {
                            endAdornment: inputValue && /*#__PURE__*/ jsx(Box, {
                                cursor: "pointer",
                                onClick: handleReset,
                                height: 24,
                                pr: "16",
                                testId: "search-reset",
                                children: /*#__PURE__*/ jsx(Icon, {
                                    name: "dismiss_substract",
                                    size: "24",
                                    color: "stroke.secondary"
                                })
                            })
                        })
                    }))
            }),
            !!(null == value ? void 0 : value.length) && /*#__PURE__*/ jsx(Box, {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "8",
                mt: "16",
                children: value.map((item, index)=>/*#__PURE__*/ jsx(Tag, {
                        label: item,
                        disabled: disabled,
                        onDelete: handleDeleteTag(index)
                    }, item))
            })
        ]
    });
};
export { MultipleSuggestedInput };
